/**
 * Section 12c — Backtest & Optimization Studio.
 *
 * The standalone /backtest page: parameter-sweep optimisation of the RockBand
 * OverView strategy over Delta history with out-of-sample testing, cost modelling
 * and no look-ahead. Distinct from the Options-AT "Backtest" tab (option strategies).
 */
import React from 'react';
import { Typography, List, ListItem, ListItemText } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const LI = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}><ListItemText primary={children} /></ListItem>
);

const Section_backtest: GuideSection = {
  id: 'backtest',
  label: 'Backtest Studio',
  group: 'pages',
  icon: <ScienceIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Backtest &amp; Optimization Studio</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Sweeps many parameter combinations of the <strong>RockBand OverView</strong> strategy over historical
        data, ranks the robust ones, and validates them out-of-sample — at <OpenInApp to="/backtest" label="Backtest" />.
        It is strictly causal: decisions on closed bars, fills at the next bar's open, costs charged every
        trade — <strong>no look-ahead, no repainting</strong>. (This is for the chart strategy; the per-option
        strategy backtest lives on the <OpenInApp to="/app-guide#options-at" label="Options AT" /> Backtest tab.)
      </Typography>

      <H id="setup">Setting up a run</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Universe</strong> — a watchlist (only its <strong>Delta</strong> symbols are backtested)
          or the built-in liquid majors. Manage the watchlist's symbols inline.</></LI>
        <LI><><strong>Data source</strong> — Delta (≈8 months history) or Binance (years; needed for 4H, whose
          HTF is Daily and wants a full bull+bear sample).</></LI>
        <LI><><strong>Timeframes</strong> — the chart TFs to test; the engine pairs each with its mapped Higher
          Timeframe (the regime filter).</></LI>
        <LI><><strong>Preset</strong> — how wide a parameter sweep to run (coarse steps guard against overfit).</></LI>
      </List>

      <H id="rules">What it optimises</H>
      <P>
        It scores each symbol with the same −100…+100 OverView trend on the chart TF (the trigger) and the
        HTF (the regime, which must agree), then tries families of entry rules (gate-flip, TF-crosses-HTF,
        dual-slope, separation filter, HTF-step) against exit rules (TF-break / regime-leave, debounced
        hysteresis, ride-the-HTF, slope-rollover) across a grid of thresholds. Each combo's entry/exit logic
        is spelled out in plain English in the in-page report.
      </P>

      <H id="metrics">Reading the report</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Expectancy (%/trade)</strong> — average net profit per trade after costs; the headline.</></LI>
        <LI><><strong>Profit factor</strong> — gross profit ÷ gross loss (&gt;1.3 is solid for trend-following).</></LI>
        <LI><><strong>Win rate</strong> — often only ~30–45% for trend-following; winners are bigger than losers.</></LI>
        <LI><><strong>Max drawdown / Sharpe / Sortino</strong> — the pain and the smoothness of the ride.</></LI>
        <LI><><strong>%Sym+ (breadth)</strong> &amp; <strong>Robustness score</strong> — how broadly the edge
          holds across symbols (the ranking favours consistency, not the single highest-return cell).</></LI>
        <LI><><strong>Out-of-sample (train / test)</strong> — optimised on earlier history, checked on a held-out
          later slice; if the test holds up the edge is more trustworthy.</></LI>
        <LI><>Equity curve vs buy&amp;hold, drawdown (underwater), monthly returns, trade-return distribution,
          and per-trade MFE/MAE.</></LI>
      </List>

      <GuideCallout kind="tip">
        Judge a rule by profit factor, breadth and out-of-sample expectancy <em>together</em> — not total
        return alone. Prefer 4H on Binance for trend strategies; tune fee + slippage to your real fills before
        trusting the numbers. The in-page <strong>Guide</strong> button has the full glossary.
      </GuideCallout>

      <GuideCallout kind="warning" title="Backtests are not promises">
        A great historical result can still fail live (regime change, costs, slippage on thin alts). Treat the
        Studio as a way to reject fragile rules and find robust ones — not as a guarantee of future returns.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'Backtest Studio', anchor: 'setup', body: 'backtest optimization studio rockband overview parameter sweep out of sample look ahead delta binance' },
    { heading: 'What it optimises', anchor: 'rules', body: 'entry exit rules gate flip tf cross htf dual slope separation hysteresis ride slope rollover' },
    { heading: 'Reading the report', anchor: 'metrics', body: 'expectancy profit factor win rate drawdown sharpe sortino breadth robustness equity curve mfe mae' },
  ],
};

export default Section_backtest;
